import { Toasts } from 'components'
import { validateInput, validateInputs } from 'utility/form-validation'
import { formatCardNumber } from 'utility/format-card-number'
import { formatExpiryDate } from 'utility/format-expiry-date'
import { WarningIcon } from '../icons'

const el = {
  root: document.querySelector<HTMLElement>('.checkout-layout--payment'),
  forms: document.querySelectorAll<HTMLFormElement>(
    '#checkout-payment-form, #checkout-payment-form-mobile'
  ),
  radios: document.querySelectorAll<HTMLInputElement>('.checkout-layout__options input'),
  submitButtons: document.querySelectorAll<HTMLButtonElement>(
    '#checkout-sidebar-submit-button, #checkout-footer-submit-button'
  ),
  terms: document.querySelectorAll<HTMLInputElement>(
    '#checkout-sidebar-terms, #checkout-mobile-terms'
  ),
  inputs: document.querySelectorAll<HTMLInputElement>(
    '.checkout-layout--payment input[required], [name="customerReferenceNumber"], [name="customerReference"]'
  ),
  cardInput: document.querySelector<HTMLInputElement>(
    '.checkout-layout--payment input[data-validation="card"]'
  ),
}

export const CheckoutPayment = {
  init: () => {
    if (!el.root || !el.forms.length) return
    el.forms.forEach((target) =>
      target.addEventListener('submit', CheckoutPayment.onSubmit)
    )
    el.radios.forEach((target) =>
      target.addEventListener('change', CheckoutPayment.onRadioChange)
    )
    el.terms.forEach((target) =>
      target.addEventListener('change', CheckoutPayment.onTermsChange)
    )
    el.inputs.forEach((field) =>
      field.addEventListener('input', CheckoutPayment.onTextfieldChange)
    )
    el.inputs.forEach((field) =>
      field.addEventListener('blur', CheckoutPayment.onTouched)
    )
    CheckoutPayment.toggleSubmitButtonState()
    CheckoutPayment.setCardPattern()
  },

  validateForm: ({ showErrors }) => {
    const someRadioChecked = Array.from(el.radios).some((x) => x.checked)
    const allTermsChecked = Array.from(el.terms).every((x) => x.checked)
    if (!someRadioChecked || !allTermsChecked) return false
    return validateInputs({ inputs: Array.from(el.inputs), showErrors })
  },

  toggleSubmitButtonState: () => {
    const isValidForm = CheckoutPayment.validateForm({ showErrors: false })
    if (!isValidForm) {
      el.submitButtons.forEach((button) => (button.disabled = true))
      return
    }

    el.submitButtons.forEach((button) => (button.disabled = false))
  },

  onTermsChange: (e: Event & { target: HTMLInputElement }) => {
    el.terms.forEach((target) => (target.checked = e.target.checked))
    CheckoutPayment.toggleSubmitButtonState()
  },

  onRadioChange: () => CheckoutPayment.toggleSubmitButtonState(),

  onTouched: (e: Event & { target: HTMLInputElement }) => {
    if (!e.target.classList.contains('touched')) {
      e.target.classList.add('touched')
    }
  },

  onTextfieldChange: (e: Event & { target: HTMLInputElement }) => {
    if (e.target.classList.contains('autovalidate')) {
      validateInput({ input: e.target, showError: true })
    }
    if (e.target.dataset.validation === 'card') {
      e.target.value = formatCardNumber(e.target.value)
    }
    if (e.target.dataset.validation === 'expiry') {
      e.target.value = formatExpiryDate(e.target.value)
    }
    CheckoutPayment.toggleSubmitButtonState()
  },

  onSubmit: async (e: Event & { target: HTMLFormElement }) => {
    e.preventDefault()

    const isValidForm = CheckoutPayment.validateForm({ showErrors: true })
    if (!isValidForm) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Þú þarft að velja greiðslumáta.',
        icon: WarningIcon(),
      })
      return
    }

    el.submitButtons.forEach((target) => {
      target.disabled = true
      target.classList.add('button--loading')
    })

    // update email address before submit if authenticated
    if (CheckoutPayment.checkIfAuthenticated()) {
      try {
        await fetch('/umbraco/surface/orders/updateemailaddress', { method: 'get' })
      } catch {
        /* fail silently */
      }
    }

    el.forms[0].submit()
  },

  setCardPattern: () => {
    if (!el.cardInput) return
    el.cardInput.pattern = [
      '[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}',
      '4[0-9]{3}-[0-9]{4}-[0-9]{4}-[0-9]{1,4}',
      '4[0-9]{3}-[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{1,3}',
      '[0-9]{12,19}',
    ].join('|')
  },

  checkIfAuthenticated: () =>
    document
      .querySelector<HTMLDivElement>('.checkout-layout--payment')
      ?.dataset.authenticated.toLowerCase() === 'true',
}
